import GraphRanges from "@/mixins/GraphRanges";
import { patientNamespace } from "@/store/modules/patient";
import _ from "lodash";
import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { MultiChartConfig } from "../shared/chart/MultiChartWithSharedRange";

@Component({})
export default class Lungs extends mixins(GraphRanges) {
  @Getter("segmentRisk", patientNamespace)
  segmentRisk!: any;

  @Getter("graph", patientNamespace)
  graph!: Function;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("atomList", patientNamespace)
  atomList!: any;

  get pf() {
    return this.graph([{ key: "PFRatio", label: "P/F Ratio" }]);
  }

  get supO2() {
    return this.graph([
      { key: "SupplementalO2Rate", label: "Supplemental O2 Rate" }
    ]);
  }

  get anyGraph() {
    return (
      this.pipPeepData.length ||
      this.fiO2SpO2Data.length ||
      this.pf.length ||
      this.supO2.length
    );
  }

  // PIP, PEEP, Driving Pressure & Lung Compliance
  get pipPeepData() {
    return this.graph([
      { key: "PIP", label: "PIP", yAxisID: "Left" },
      { key: "PEEP", label: "PEEP", yAxisID: "Left" },
      { key: "DrivingPressure", label: "ΔP", yAxisID: "Left" },
      { key: "LungCompliance", label: "Lung Compliance", yAxisID: "Right" }
    ]);
  }

  get pipPeepLabel() {
    const labels: Array<string> = [];
    this.pipPeepData.forEach((d: any) => {
      labels.push(d.label);
    });
    return labels;
  }

  get pipPeepConfig() {
    const pressureScale = {
      id: "Left",
      position: "left",
      type: "linear",
      ticks: {
        suggestedMax: 30,
        stepSize: 10,
        maxTicksLimit: 4,
        min: 0
      }
    };
    const resultScale = {
      id: "Right",
      position: "right",
      type: "linear",
      ticks: {
        min: 0,
        suggestedMax: 40,
        maxTicksLimit: 4
      }
    };
    const yAxes: Array<any> = [];
    this.pipPeepLabel.forEach((label: string) => {
      switch (label) {
        case "PIP":
          yAxes.push(pressureScale);
          break;
        case "PEEP":
          yAxes.push(pressureScale);
          break;
        case "ΔP":
          yAxes.push(pressureScale);
          break;
        case "Lung Compliance":
          yAxes.push(resultScale);
          break;
      }
    });
    return {
      scales: { yAxes },
      tooltips: {
        mode: "index"
      }
    };
  }

  // SpO2 FiO2 & Resp Rate
  get fiO2SpO2Data() {
    return this.graph([
      { key: "SpO2", label: "SpO2", yAxisID: "Left" },
      { key: "FiO2", label: "FiO2", yAxisID: "Left" },
      { key: "RespiratoryRate", label: "Respiratory Rate", yAxisID: "Right" }
    ]);
  }

  get fiO2SpO2Label() {
    const labels: Array<string> = [];
    this.fiO2SpO2Data.forEach((d: any) => {
      labels.push(d.label);
    });
    return labels;
  }
  mounted() {
    this.fiO2SpO2Config;
  }
  get fiO2SpO2Config() {
    const o2Scale = {
      id: "Left",
      position: "left",
      type: "linear",
      ticks: {
        min: 0,
        maxTicksLimit: 4,
        suggestedMax: 100
      }
    };
    const respScale = {
      id: "Right",
      position: "right",
      type: "linear",
      ticks: {
        min: 0,
        maxTicksLimit: 4,
        suggestedMax: 40
      }
    };
    const yAxes: Array<any> = [];
    this.fiO2SpO2Label.forEach((label: string) => {
      switch (label) {
        case "FiO2":
          yAxes.push(o2Scale);
          break;
        case "SpO2":
          yAxes.push(o2Scale);
          break;
        case "Respiratory Rate":
          yAxes.push(respScale);
          break;
      }
    });
    return { scales: { yAxes } };
  }

  get charts(): MultiChartConfig[] {
    const chartList: MultiChartConfig[] = [];
    if (this.pipPeepData.length) {
      chartList.push({
        title:
          "PIP, PEEP, ΔP (Driving Pressure= PIP-PEEP)(cmH<sub>2</sub>O) & Lung Compliance (mL/cmH<sub>2</sub>O)*",
        data: this.pipPeepData,
        flexBasis: "300px",
        library: this.pipPeepConfig
      });
    }

    if (this.fiO2SpO2Data.length) {
      chartList.push({
        title:
          "SpO<sub>2</sub>, FiO<sub>2</sub> (%) & Respiratory Rate (Breaths/Minute)*",
        data: this.fiO2SpO2Data,
        flexBasis: "300px",
        library: this.fiO2SpO2Config
      });
    }

    if (this.pf.length) {
      chartList.push({
        title: "P/F Ratio",
        data: this.pf,
        min: 0,
        suggestedMax: 500
      });
    }

    if (this.supO2.length) {
      chartList.push({
        title: "Supplemental O<sub>2</sub> Rate (L/min)",
        data: this.supO2,
        min: 0,
        suggestedMax: 10
      });
    }
    return chartList;
  }

  get medianDisclaimer() {
    const medians: number[] = [
      _.get(this, "atomList['pip'].trend", 1),
      _.get(this, "atomList['peep'].trend", 1),
      _.get(this, "atomList['drivingPressure'].trend", 1),
      _.get(this, "atomList['lungCompliance'].trend", 1),
      _.get(this, "atomList['spo2'].trend", 1),
      _.get(this, "atomList['fiO2'].trend", 1),
      _.get(this, "atomList['lungRate'].trend", 1)
    ];

    return (
      "*Data points displayed for " +
      this.pipPeepLabel.join(", ") +
      ", " +
      this.fiO2SpO2Label.join(", ") +
      " represent the median over (" +
      medians.join(",") +
      ") minutes respectively and are saved to the graph every 15 minutes."
    );
  }

  chartLabels = [
    { atom: "pip", label: "PIP" },
    { atom: "peep", label: "PEEP" },
    { atom: "drivingPressure", label: "Driving Pressure" },
    { atom: "lungRate", label: "Respiratory Rate" },
    { atom: "fiO2", label: "FiO2" }
  ];
}
